import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, ToggleLeft, ToggleRight, Save, X, ChefHat, Carrot, Apple, Image, Trash2, Languages } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';

import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface Supplier {
  id: number;
  username: string;
  displayName: string | null;
  supplierTag: string | null;
  label: string | null;
}

interface Product {
  id: number;
  category: 'item' | 'vegetable' | 'fruit';
  productCode: string | null;
  name: string;
  nameEn?: string | null;
  description: string | null;
  descriptionEn?: string | null;
  price: number | null;
  originalPrice: number | null;
  isActive: number;
  stockQuantity: number;
  sortOrder: number;
  imageUrl: string | null;
  maxSelect: number;
  isApproved: number;
  reviewStatus?: string | null;
  reviewComment?: string | null;
  submittedAt?: number | null;
  supplierId?: number | null;
  supplierType?: string | null;
  supplier?: { id?: number; displayName?: string; supplierTag?: string; label?: string } | null;
}

// HKSEMALL Phase 1: review_status 標籤
const REVIEW_STATUS_META: Record<string, { label: string; className: string }> = {
  draft: { label: '草稿', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  pending_review: { label: '待審批', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300' },
  needs_revision: { label: '需修改', className: 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300' },
  approved: { label: '已批核', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300' },
  rejected: { label: '已否決', className: 'bg-red-200 text-red-800 dark:bg-red-900/40 dark:text-red-200' },
  active: { label: '已上架', className: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' },
  paused: { label: '已暫停', className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  discontinued: { label: '已停售', className: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
};

const SUPPLIER_TYPE_LABELS: Record<string, string> = {
  general: '一般',
  social_enterprise: '社企',
  sen_school: 'SEN 學校',
  commercial: '商業',
};



interface MediaItem {
  key: string;
  name: string;
  size: number;
  uploaded: string;
  url: string;
}

const categoryLabels: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  item: { label: '餸菜', icon: ChefHat, color: 'text-orange-600 bg-orange-50' },
  vegetable: { label: '蔬菜', icon: Carrot, color: 'text-green-600 bg-green-50' },
  fruit: { label: '水果', icon: Apple, color: 'text-red-600 bg-red-50' },
};

const AdminProducts: React.FC = () => {
  const navigate = useNavigate();
  const { can, user } = usePermissions();
  const canWriteProducts = can(PERMISSIONS.PRODUCTS_WRITE);
  const isSupplier = user?.role === 'supplier';
  const isAdmin = !isSupplier; // missing role falls back to admin
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusTab, setStatusTab] = useState<'all' | 'reviewQueue' | 'active' | 'inactive'>('all');
  const [activeTab, setActiveTab] = useState<'all' | 'item' | 'vegetable' | 'fruit'>('all');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Product>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaList, setMediaList] = useState<MediaItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    category: 'item',
    name: '',
    description: '',
    price: null,
    isActive: 1,
    stockQuantity: 50,
    sortOrder: 0,
  });

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const query = statusTab === 'reviewQueue'
        ? '?reviewStatus=pending_review'
        : statusTab !== 'all' ? `?status=${statusTab}` : '';
      const res = await apiFetch(`/api/public/admin/products${query}`);
      if (res.ok) {
        setProducts(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      setError('獲取產品失敗');
    } finally {
      setLoading(false);
    }
  }, [statusTab, navigate]);

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
  }, [fetchProducts]);

  const fetchMedia = async () => {
    try {
      const res = await apiFetch('/api/public/admin/media');
      if (res.ok) {
        setMediaList(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      setError('獲取媒體庫失敗');
    }
  };

  const fetchSuppliers = async () => {
    if (!isAdmin) return;
    try {
      const res = await apiFetch('/api/public/admin/suppliers');
      if (res.ok) {
        setSuppliers(await res.json());
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      console.error('獲取供應商列表失敗', e);
    }
  };

  // Extract original filename from media key like "media/1782221816202_WhatsApp_Image_xxx.jpg"
  const extractOriginalName = (key: string): string => {
    const fileName = key.replace(/^media\//, '');
    const match = fileName.match(/^(\d+)_(.+?)(\.[^.]+)?$/);
    if (match) {
      return match[2].replace(/_/g, ' ');
    }
    return fileName.replace(/\.[^.]+$/, '').replace(/_/g, ' ');
  };

  const usedImageUrls = new Set(products.map(p => p.imageUrl).filter(Boolean));
  const availableMedia = mediaList.filter(m => !usedImageUrls.has(m.url));

  const handleToggle = async (id: number, current: number) => {
    // 樂觀更新本地狀態，避免每次切換都整頁刷新（方便連續上下架多個產品）；失敗時回滾
    const next = current ? 0 : 1;
    setProducts(prev => prev.map(p => (p.id === id ? { ...p, isActive: next } : p)));
    const rollback = () => setProducts(prev => prev.map(p => (p.id === id ? { ...p, isActive: current } : p)));
    try {
      const res = await apiFetch(`/api/public/admin/products/${id}/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !current }),
      });
      if (!res.ok) {
        rollback();
        const data = await res.json().catch(() => ({}));
        setError((data as any).error || '操作失敗');
      }
    } catch (e) {
      rollback();
      setError('操作失敗');
    }
  };

  const handleApprove = async (id: number) => {
    try {
      const res = await apiFetch(`/api/public/admin/products/${id}/approve`, {
        method: 'POST',
      });
      if (res.ok) {
        fetchProducts();
      } else {
        setError('審批失敗');
      }
    } catch (e) {
      setError('審批失敗');
    }
  };

  // HKSEMALL Phase 1: 審批動作（批准 / 退回修改 / 否決）
  const handleReview = async (id: number, action: 'approve' | 'revise' | 'reject') => {
    let comment: string | undefined;
    if (action === 'revise' || action === 'reject') {
      const input = window.prompt(action === 'revise' ? '請輸入退回修改意見：' : '請輸入否決原因：');
      if (input === null) return;
      if (!input.trim()) {
        setError('退回修改或否決必須填寫意見');
        return;
      }
      comment = input.trim();
    }
    try {
      const res = await apiFetch(`/api/public/admin/products/${id}/review`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, comment }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        fetchProducts();
      } else {
        setError((data as any).error || '審批失敗');
      }
    } catch (e) {
      setError('審批失敗');
    }
  };

  // HKSEMALL Phase 1: 供應商提交審批
  const handleSubmitReview = async (id: number) => {
    try {
      const res = await apiFetch(`/api/public/supplier/products/${id}/submit`, {
        method: 'POST',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        fetchProducts();
      } else {
        setError((data as any).error || '提交失敗');
      }
    } catch (e) {
      setError('提交失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定刪除此產品？')) return;
    try {
      const res = await apiFetch(`/api/public/admin/products/${id}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        fetchProducts();
      } else {
        setError('刪除失敗');
      }
    } catch (e) {
      setError('刪除失敗');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingId(product.id);
    setEditForm({ ...product });
  };

  const handleSave = async () => {
    if (!editForm.name) return;
    try {
      const url = isSupplier
        ? `/api/public/supplier/products/${editingId}`
        : `/api/public/admin/products/${editingId}`;
      // Supplier 只能更新自己產品的基本資料與庫存，避免送出 admin-only 欄位
      const payload = isSupplier
        ? {
            name: editForm.name,
            nameEn: editForm.nameEn,
            productCode: editForm.productCode,
            description: editForm.description,
            descriptionEn: editForm.descriptionEn,
            price: editForm.price,
            originalPrice: editForm.originalPrice,
            stockQuantity: editForm.stockQuantity,
            sortOrder: editForm.sortOrder,
            imageUrl: editForm.imageUrl,
            maxSelect: editForm.maxSelect,
          }
        : editForm;
      const res = await apiFetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setEditingId(null);
        fetchProducts();
      } else {
        const data = await res.json().catch(() => ({}));
        setError((data as any).error || `保存失敗 (${res.status})`);
      }
    } catch (e) {
      setError('保存失敗');
    }
  };

  const [translatingIds, setTranslatingIds] = useState<Set<number>>(new Set());

  // AI 翻譯：補齊單一產品的空白英文字段（name_en / description_en）
  const handleAiTranslate = async (productId: number) => {
    setTranslatingIds(prev => new Set(prev).add(productId));
    try {
      const res = await apiFetch(`/api/public/admin/products/${productId}/ai-translate`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if ((data as any).skipped === 'already_filled') {
          alert('英文欄位已齊，無需翻譯');
        } else if ((data as any).skipped) {
          alert(`跳過：${(data as any).skipped}`);
        } else if ((data as any).updated) {
          alert(`翻譯完成：${(data as any).nameEn || '(名稱已有)'}${(data as any).descriptionEn ? ' ＋ 描述' : ''}`);
          fetchProducts();
        }
      } else {
        alert((data as any).error || `翻譯失敗 (${res.status})`);
      }
    } catch {
      alert('網絡錯誤，請稍後再試');
    } finally {
      setTranslatingIds(prev => { const n = new Set(prev); n.delete(productId); return n; });
    }
  };

  const handleAdd = async (submit = false) => {
    if (!newProduct.name) return;
    try {
      const url = isSupplier ? '/api/public/supplier/products' : '/api/public/admin/products';
      const res = await apiFetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newProduct, ...(isSupplier ? { submit } : {}) }),
      });
      if (res.ok) {
        setShowAddForm(false);
        setNewProduct({ category: 'item', name: '', description: '', price: null, isActive: 1, stockQuantity: 50, sortOrder: 0 });
        fetchProducts();
      }
    } catch (e) {
      setError('創建失敗');
    }
  };

  const sortedProducts = [...products].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  const filteredProducts = activeTab === 'all'
    ? sortedProducts
    : sortedProducts.filter(p => p.category === activeTab);

  const statusTabs: { key: typeof statusTab; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'reviewQueue', label: '審批隊列' },
    { key: 'active', label: '已上架' },
    { key: 'inactive', label: '已下架' },
  ];

  const categoryTabs: { key: typeof activeTab; label: string }[] = [
    { key: 'all', label: '全部' },
    { key: 'item', label: '餸菜' },
    { key: 'vegetable', label: '蔬菜' },
    { key: 'fruit', label: '水果' },
  ];

  // 向後相容：舊資料冇 review_status 時按 is_approved / is_active 推導
  const getEffectiveReviewStatus = (product: Product): string => {
    if (product.reviewStatus) return product.reviewStatus;
    if (product.isActive) return 'active';
    if (product.supplierId != null && product.isApproved === 0) return 'pending_review';
    return 'paused';
  };

  const renderStatusBadge = (product: Product) => {
    const badges: React.ReactNode[] = [];

    const status = getEffectiveReviewStatus(product);
    const meta = REVIEW_STATUS_META[status] || REVIEW_STATUS_META.draft;
    badges.push(
      <span key="review" className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${meta.className}`}>
        {meta.label}
      </span>
    );

    // Supplier type 標籤（admin 視圖）
    if (!isSupplier && product.supplierType && product.supplierType !== 'general') {
      badges.push(
        <span key="stype" className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300">
          {SUPPLIER_TYPE_LABELS[product.supplierType] || product.supplierType}
        </span>
      );
    }

    // Price warning
    if (!product.price || product.price <= 0) {
      badges.push(<span key="no-price" className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300">未定價</span>);
    }

    return <div className="flex flex-wrap gap-1">{badges}</div>;
  };

  return (
    <AdminLayout currentPage="products">
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
            <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Status tabs (admin/super_admin only) */}
        {isAdmin && (
          <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 mb-4 rounded-t-lg">
            <nav className="flex space-x-1 px-2" aria-label="Status tabs">
              {statusTabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setStatusTab(tab.key)}
                  className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                    statusTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {/* Category tabs */}
        <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 mb-6 rounded-t-lg">
          <nav className="flex space-x-1 px-2" aria-label="Category tabs">
            {categoryTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Add Button */}
        {canWriteProducts && (
          <div className="mb-6">
            <button
              onClick={() => {
                if (!showAddForm) {
                  const maxOrder = products.reduce((max, p) => Math.max(max, p.sortOrder || 0), 0);
                  setNewProduct({
                    category: 'item',
                    name: '',
                    description: '',
                    price: null,
                    isActive: 1,
                    stockQuantity: 50,
                    sortOrder: maxOrder + 10,
                  });
                  fetchMedia();
                  setShowMediaPicker(false);
                }
                setShowAddForm(!showAddForm);
              }}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-5 h-5 mr-2" />
              {showAddForm ? '取消' : '新增產品'}
            </button>
          </div>
        )}

        {/* Add Form */}
        {showAddForm && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6 border-l-4 border-blue-500">
            <h3 className="text-lg font-bold dark:text-white">新增產品</h3>
            {isSupplier && (
              <p className="text-sm text-orange-600 mt-1">修改後將重新進入審批</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">類別</label>
                <select
                  value={newProduct.category}
                  onChange={e => setNewProduct({ ...newProduct, category: e.target.value as any })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                >
                  <option value="item">餸菜</option>
                  <option value="vegetable">蔬菜</option>
                  <option value="fruit">水果</option>
                </select>
              </div>
              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">供應商</label>
                  <select
                    value={newProduct.supplierId ?? ''}
                    onChange={e => setNewProduct({ ...newProduct, supplierId: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  >
                    <option value="">無（好餸自產）</option>
                    {suppliers.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.displayName || s.username}{s.label ? ` (${s.label})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">名稱</label>
                <input
                  value={newProduct.name}
                  onChange={e => setNewProduct({ ...newProduct, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="產品名稱"
                />
                <input
                  value={newProduct.nameEn || ''}
                  onChange={e => setNewProduct({ ...newProduct, nameEn: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
                  placeholder="英文名稱（留空由 AI 自動翻譯）"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">價格</label>
                <input
                  type="number"
                  value={newProduct.price || ''}
                  onChange={e => setNewProduct({ ...newProduct, price: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="HK$"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
                <input
                  value={newProduct.description || ''}
                  onChange={e => setNewProduct({ ...newProduct, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="產品描述"
                />
                <input
                  value={newProduct.descriptionEn || ''}
                  onChange={e => setNewProduct({ ...newProduct, descriptionEn: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
                  placeholder="英文描述（留空由 AI 自動翻譯）"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                <input
                  type="number"
                  value={newProduct.sortOrder || 0}
                  onChange={e => setNewProduct({ ...newProduct, sortOrder: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">庫存</label>
                <input
                  type="number"
                  value={newProduct.stockQuantity ?? ''}
                  onChange={e => setNewProduct({ ...newProduct, stockQuantity: e.target.value ? parseInt(e.target.value) : 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="數量"
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">圖片 URL</label>
                <input
                  value={newProduct.imageUrl || ''}
                  onChange={e => setNewProduct({ ...newProduct, imageUrl: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  placeholder="https://...（可從媒體庫複製）"
                />
                <button
                  type="button"
                  onClick={() => setShowMediaPicker(!showMediaPicker)}
                  className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                  <Image size={16} />
                  {showMediaPicker ? '隱藏媒體庫' : `從媒體庫選擇圖片 (${availableMedia.length})`}
                </button>

                {showMediaPicker && (
                  <div className="mt-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
                    {availableMedia.length === 0 ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {mediaList.length === 0 ? '媒體庫為空，請先上傳圖片。' : '所有圖片已被使用。'}
                      </p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3 max-h-64 overflow-y-auto">
                        {availableMedia.map(item => {
                          const originalName = extractOriginalName(item.key);
                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => {
                                setNewProduct({
                                  ...newProduct,
                                  imageUrl: item.url,
                                  name: originalName,
                                });
                                setShowMediaPicker(false);
                              }}
                              className="group relative aspect-square rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              title={originalName}
                            >
                              <img
                                src={item.url}
                                alt={originalName}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                {originalName}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              {isSupplier ? (
                <>
                  <button onClick={() => handleAdd(false)} className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">儲存草稿</button>
                  <button onClick={() => handleAdd(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">創建並提交審批</button>
                </>
              ) : (
                <button onClick={() => handleAdd(false)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">創建</button>
              )}
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600">取消</button>
            </div>
          </div>
        )}

        {/* Products Table */}
        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">加載中...</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">排序</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">類別</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">編號</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">名稱</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">描述</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">價格</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">庫存</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">圖片</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProducts.map(product => {
                  const cat = categoryLabels[product.category] || categoryLabels.item;
                  const CatIcon = cat.icon;
                  const isEditing = editingId === product.id;
                  const isPending = product.supplierId != null && product.isActive === 0 && product.isApproved === 0;
                  const reviewStatus = getEffectiveReviewStatus(product);
                  const canSubmitReview = isSupplier && ['draft', 'needs_revision', 'rejected'].includes(reviewStatus);

                  return (
                    <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.sortOrder ?? 0}
                            onChange={e => setEditForm({ ...editForm, sortOrder: parseInt(e.target.value) || 0 })}
                            className="w-16 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-bold text-gray-600 dark:text-gray-300">
                            {product.sortOrder || 0}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <select
                            value={editForm.category || 'item'}
                            onChange={e => setEditForm({ ...editForm, category: e.target.value as Product['category'] })}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          >
                            <option value="item">餸菜</option>
                            <option value="vegetable">蔬菜</option>
                            <option value="fruit">水果</option>
                          </select>
                        ) : (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${cat.color}`}>
                            <CatIcon size={14} />
                            {cat.label}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        {isEditing ? (
                          <input
                            value={editForm.productCode || ''}
                            onChange={e => setEditForm({ ...editForm, productCode: e.target.value })}
                            className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            placeholder="編號"
                          />
                        ) : (
                          <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-xs">{product.productCode || '-'}</code>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div>
                            <input
                              value={editForm.name || ''}
                              onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            />
                            <input
                              value={editForm.nameEn || ''}
                              onChange={e => setEditForm({ ...editForm, nameEn: e.target.value })}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-800 dark:text-white"
                              placeholder="英文名稱（留空 AI 翻譯）"
                            />
                            {!isSupplier && (
                              <select
                                value={editForm.supplierId ?? ''}
                                onChange={e => setEditForm({ ...editForm, supplierId: e.target.value ? Number(e.target.value) : null })}
                                className="w-full mt-2 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                              >
                                <option value="">無（好餸自產）</option>
                                {suppliers.map(s => (
                                  <option key={s.id} value={s.id}>
                                    {s.displayName || s.username}{s.label ? ` (${s.label})` : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                            {isSupplier && (
                              <p className="text-xs text-orange-600 mt-1">修改後將重新進入審批</p>
                            )}
                          </div>
                        ) : (
                          <div>
                            <span className="font-medium text-gray-900 dark:text-white">{product.name}</span>
                            {product.nameEn ? (
                              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{product.nameEn}</div>
                            ) : (
                              <div className="text-xs text-amber-500 mt-0.5">未譯</div>
                            )}
                            {product.supplierId != null && product.supplier ? (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{product.supplier.displayName || product.supplier.label}</div>
                            ) : (
                              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">好餸自產</div>
                            )}
                            {isSupplier && getEffectiveReviewStatus(product) === 'needs_revision' && (
                              <div className="mt-1.5 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
                                <span className="font-bold">需修改：</span>{product.reviewComment || '請按審批意見修改後重新提交'}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {isEditing ? (
                          <div>
                            <input
                              value={editForm.description || ''}
                              onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                              className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            />
                            <input
                              value={editForm.descriptionEn || ''}
                              onChange={e => setEditForm({ ...editForm, descriptionEn: e.target.value })}
                              className="w-full mt-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs bg-white dark:bg-gray-800 dark:text-white"
                              placeholder="英文描述（留空 AI 翻譯）"
                            />
                          </div>
                        ) : (
                          product.description || '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.price || ''}
                            onChange={e => setEditForm({ ...editForm, price: e.target.value ? parseInt(e.target.value) : null })}
                            className="w-24 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          product.price ? `HK$${product.price}` : '-'
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.stockQuantity ?? 0}
                            onChange={e => setEditForm({ ...editForm, stockQuantity: parseInt(e.target.value) || 0 })}
                            className="w-20 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                          />
                        ) : (
                          <span className={`font-medium ${
                            product.stockQuantity === 0 ? 'text-red-600' :
                            product.stockQuantity < 15 ? 'text-orange-500' : 'text-green-600'
                          }`}>
                            {product.stockQuantity}
                            {product.stockQuantity === 0 && <span className="ml-1 text-xs">(缺貨)</span>}
                            {product.stockQuantity > 0 && product.stockQuantity < 15 && <span className="ml-1 text-xs">(即將售罄)</span>}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {isEditing ? (
                          <input
                            value={editForm.imageUrl || ''}
                            onChange={e => setEditForm({ ...editForm, imageUrl: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 dark:text-white"
                            placeholder="圖片URL"
                          />
                        ) : (
                          product.imageUrl ? (
                            <img src={product.imageUrl} alt={product.name} className="w-10 h-10 object-cover rounded" />
                          ) : (
                            <span className="text-gray-400 dark:text-gray-500 text-xs">-</span>
                          )
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!isEditing && renderStatusBadge(product)}
                      </td>
                      <td className="px-4 py-3">
                        {canWriteProducts ? (
                          isEditing ? (
                            <div className="flex gap-2">
                              <button onClick={handleSave} className="text-green-600 hover:text-green-800"><Save size={18} /></button>
                              <button onClick={() => setEditingId(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              {!isSupplier && !isPending && (
                                <button
                                  onClick={() => handleToggle(product.id, product.isActive)}
                                  className={`flex items-center gap-1 text-sm font-medium ${product.isActive ? 'text-green-600' : 'text-gray-400'}`}
                                  title={product.isActive ? '停用' : '啟用'}
                                >
                                  {product.isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                </button>
                              )}
                              <button onClick={() => handleEdit(product)} className="text-blue-600 hover:text-blue-800" title="編輯">
                                <Edit2 size={18} />
                              </button>
                              <button
                                onClick={() => handleAiTranslate(product.id)}
                                disabled={translatingIds.has(product.id)}
                                className="text-purple-600 hover:text-purple-800 disabled:opacity-40"
                                title="AI 翻譯空白英文字段"
                              >
                                <Languages size={18} className={translatingIds.has(product.id) ? 'animate-pulse' : ''} />
                              </button>
                              {canSubmitReview && (
                                <button
                                  onClick={() => handleSubmitReview(product.id)}
                                  className="text-xs px-2 py-1 bg-orange-600 text-white rounded hover:bg-orange-700"
                                >
                                  提交審批
                                </button>
                              )}
                              {isAdmin && reviewStatus === 'pending_review' && (
                                <>
                                  <button
                                    onClick={() => handleReview(product.id, 'approve')}
                                    className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                  >
                                    批准
                                  </button>
                                  <button
                                    onClick={() => handleReview(product.id, 'revise')}
                                    className="text-xs px-2 py-1 bg-yellow-600 text-white rounded hover:bg-yellow-700"
                                  >
                                    退回修改
                                  </button>
                                  <button
                                    onClick={() => handleReview(product.id, 'reject')}
                                    className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                                  >
                                    否決
                                  </button>
                                </>
                              )}
                              {isAdmin && (reviewStatus === 'approved' || isPending) && (
                                <button
                                  onClick={() => handleApprove(product.id)}
                                  className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                >
                                  批准上架
                                </button>
                              )}
                              {(isAdmin || !product.isActive) && (
                                <button
                                  onClick={() => handleDelete(product.id)}
                                  className="text-red-600 hover:text-red-800"
                                  title="刪除"
                                >
                                  <Trash2 size={18} />
                                </button>
                              )}
                            </div>
                          )
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredProducts.length === 0 && (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">沒有產品</div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
